import { confirm } from 'devextreme/ui/dialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteNote, getGrantNotes } from '../../../../api/v2/NotesApi';
import { GrantDto } from '../../../../dtos/GrantDto';
import { NoteDto } from '../../../../dtos/NoteDto';
import { useAppContext } from '../../../shared/AppContext';
import { TreeSelectNode } from '../../../shared/TreeSelect';

export interface NotesControllerProps {
    grantData: GrantDto;
    // Incremented by the parent (the Add Note button lives on the tab row) to trigger a reload.
    refreshToken?: number;
    // Called after an in-tab edit/delete so the parent can refresh everything that shows notes
    // (the grid and the progress timeline tic), not just this grid.
    onNotesChanged?: () => void;
}

export interface NotesViewProps {
    grantData: GrantDto;
    loading: boolean;
    notes: NoteDto[];
    locationNodes: TreeSelectNode[];
    selectedLocationKeys: string[];
    onLocationKeysChange: (keys: string[]) => void;
    searchText: string;
    onSearchTextChange: (text: string) => void;
    // Notes are now role-shared, so a listed note may belong to someone else. Only the author may
    // edit/delete, so the grid uses this to show those buttons only on the current user's notes.
    currentUserId: string;
    // The note currently being edited (drives the edit dialog), and its lifecycle handlers.
    editingNote: NoteDto | null;
    onEditNote: (note: NoteDto) => void;
    onCloseEdit: () => void;
    onNoteSaved: () => void;
    onDeleteNote: (note: NoteDto) => void;
}

// The single location a note belongs to. Matches the node ids built below so the filter is exact.
const noteLocationKey = (n: NoteDto): string =>
    n.stepId ? `step:${n.stepId}` : n.phaseId ? `phase:${n.phaseId}` : 'grant';

// Builds the module → task → subtask tree from the locations the loaded notes actually use,
// so the filter only offers locations that have notes. Node ids are the same keys we match on.
const buildLocationNodes = (notes: NoteDto[]): TreeSelectNode[] => {
    const nodes: TreeSelectNode[] = [];
    const seen = new Set<string>();

    for (const n of notes) {
        // Grant-level notes (no task/subtask) aren't offered as a filter option.
        if (!n.phaseId && !n.stepId) continue;

        const moduleName = n.module ?? 'Other';
        const moduleId = `m:${moduleName}`;
        if (!seen.has(moduleId)) {
            seen.add(moduleId);
            nodes.push({ id: moduleId, parentId: null, text: moduleName, expanded: true });
        }

        // A subtask note carries its parent task too, so nest under the task when we have it.
        let phaseNodeId = moduleId;
        if (n.phaseId) {
            phaseNodeId = `phase:${n.phaseId}`;
            if (!seen.has(phaseNodeId)) {
                seen.add(phaseNodeId);
                nodes.push({ id: phaseNodeId, parentId: moduleId, text: n.phase ?? '(task)', expanded: true });
            }
        }

        if (n.stepId) {
            const stepNodeId = `step:${n.stepId}`;
            if (!seen.has(stepNodeId)) {
                seen.add(stepNodeId);
                nodes.push({ id: stepNodeId, parentId: phaseNodeId, text: n.step ?? '(subtask)' });
            }
        }
    }

    return nodes;
};

export const useNotesController = ({ grantData, refreshToken, onNotesChanged }: NotesControllerProps): NotesViewProps => {
    const app = useAppContext();
    const [notes, setNotes] = useState<NoteDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedLocationKeys, setSelectedLocationKeys] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');
    const [editingNote, setEditingNote] = useState<NoteDto | null>(null);

    const reload = useCallback(async () => {
        if (!grantData?.id) return;
        setLoading(true);
        const result = await getGrantNotes(grantData.id);
        if (result?.data) setNotes(result.data);
        setLoading(false);
    }, [grantData?.id]);

    // Prefer the parent's refresh (reloads the grid via refreshToken AND the timeline); fall back to
    // a local grid reload if no parent handler is wired.
    const notifyChanged = useCallback(() => {
        if (onNotesChanged) onNotesChanged();
        else reload();
    }, [onNotesChanged, reload]);

    const onDeleteNote = useCallback(async (note: NoteDto) => {
        const ok = await confirm('Delete this note? This cannot be undone.', 'Delete Note');
        if (!ok) return;
        const result = await deleteNote(note.id);
        if (result && !result.error) notifyChanged();
    }, [notifyChanged]);

    const onNoteSaved = useCallback(() => {
        setEditingNote(null);
        notifyChanged();
    }, [notifyChanged]);

    useEffect(() => {
        reload();
    }, [reload, refreshToken]);

    // Drop filters when switching grants — the location keys and search won't apply elsewhere.
    useEffect(() => {
        setSelectedLocationKeys([]);
        setSearchText('');
    }, [grantData?.id]);

    const locationNodes = useMemo(() => buildLocationNodes(notes), [notes]);

    const filteredNotes = useMemo(() => {
        if (!selectedLocationKeys.length) return notes;
        const keys = new Set(selectedLocationKeys);
        return notes.filter(n => keys.has(noteLocationKey(n)));
    }, [notes, selectedLocationKeys]);

    return {
        grantData,
        loading,
        notes: filteredNotes,
        locationNodes,
        selectedLocationKeys,
        onLocationKeysChange: setSelectedLocationKeys,
        searchText,
        onSearchTextChange: setSearchText,
        currentUserId: app.userId,
        editingNote,
        onEditNote: setEditingNote,
        onCloseEdit: () => setEditingNote(null),
        onNoteSaved,
        onDeleteNote,
    };
};
